/**
 * CriticalAlertsCard — dashboard widget showing top alerts from /api/alerts/all
 *
 * Shows the most critical issues in one glance on the Command Centre so
 * you don't have to navigate to the Smart Alerts page to discover them.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowRight, ShieldCheck } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

interface Alert {
  id: string
  category: string
  severity: 'CRITICAL' | 'HIGH' | 'MODERATE' | 'LOW'
  title: string
  message: string
  action?: string
  action_url?: string
  value_gbp?: number
}

interface AlertsResponse {
  alerts: Alert[]
  summary: {
    critical_count: number
    high_count: number
    moderate_count: number
    low_count: number
  }
}

const SEVERITY_STYLES: Record<string, { badge: string; border: string; text: string }> = {
  CRITICAL: {
    badge: 'bg-rose/20 text-rose-light border-rose/40',
    border: 'border-rose/40',
    text: 'text-rose-light',
  },
  HIGH: {
    badge: 'bg-amber/20 text-amber-light border-amber/40',
    border: 'border-amber/40',
    text: 'text-amber-light',
  },
  MODERATE: {
    badge: 'bg-blue/20 text-blue-light border-blue/40',
    border: 'border-blue/30',
    text: 'text-blue-light',
  },
  LOW: {
    badge: 'bg-slate-700/50 text-slate-400 border-slate-600',
    border: 'border-border',
    text: 'text-text-secondary',
  },
}

const fmt = (n?: number) =>
  n !== undefined ? `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}` : ''

export default function CriticalAlertsCard() {
  const [data, setData] = useState<AlertsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await api.get<AlertsResponse>('/api/alerts/all')
        if (!cancelled) setData(res.data)
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load alerts')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <div className="card p-5">
        <div className="h-6 w-40 bg-surface-hover rounded animate-pulse mb-3" />
        <div className="space-y-2">
          <div className="h-12 bg-surface-hover rounded animate-pulse" />
          <div className="h-12 bg-surface-hover rounded animate-pulse" />
        </div>
      </div>
    )
  }

  if (error) {
    // Silently show a minimal error card — don't break the dashboard
    return (
      <div className="card p-4 text-xs text-text-muted">
        Alerts feed unavailable — <Link to="/app/alerts" className="text-blue-light hover:underline">open Smart Alerts</Link>
      </div>
    )
  }

  if (!data || data.alerts.length === 0) {
    return (
      <div className="card p-4 border-emerald/30 bg-emerald/5 flex items-center gap-3">
        <ShieldCheck size={18} className="text-emerald shrink-0" aria-hidden />
        <div className="flex-1">
          <p className="text-sm font-semibold text-text-primary">All clear</p>
          <p className="text-xs text-text-muted">
            No critical issues detected across your household. Keep it up.
          </p>
        </div>
        <Link
          to="/app/alerts"
          className="text-[11px] text-blue-light hover:underline shrink-0"
        >
          View all →
        </Link>
      </div>
    )
  }

  // Show up to 3 most critical alerts
  const topAlerts = data.alerts.slice(0, 3)
  const totalCount = data.alerts.length

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} className="text-rose-light" aria-hidden />
          <h2 className="font-semibold text-text-primary text-sm">Critical Alerts</h2>
          {data.summary.critical_count > 0 && (
            <span className="badge badge-red text-[10px]">
              {data.summary.critical_count} critical
            </span>
          )}
          {data.summary.high_count > 0 && (
            <span className="badge badge-amber text-[10px]">
              {data.summary.high_count} high
            </span>
          )}
        </div>
        <Link
          to="/app/alerts"
          className="text-[11px] text-blue-light hover:underline"
        >
          View all {totalCount} →
        </Link>
      </div>

      <div className="space-y-2">
        {topAlerts.map(alert => {
          const styles = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.LOW
          const Wrapper = alert.action_url ? Link : 'div'
          const wrapperProps = alert.action_url
            ? { to: alert.action_url, className: 'block' }
            : {}

          return (
            <Wrapper
              key={alert.id}
              {...(wrapperProps as any)}
            >
              <div
                className={clsx(
                  'p-3 rounded-lg border flex items-start gap-3 transition-colors',
                  styles.border,
                  alert.action_url && 'hover:bg-surface-hover/50 cursor-pointer',
                )}
              >
                <span className={clsx(
                  'text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 mt-0.5',
                  styles.badge,
                )}>
                  {alert.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-text-primary">{alert.title}</p>
                  <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{alert.message}</p>
                  {alert.action && (
                    <p className="text-[11px] text-blue-light mt-1 flex items-center gap-1">
                      <ArrowRight size={10} aria-hidden />
                      {alert.action}
                    </p>
                  )}
                </div>
                {alert.value_gbp !== undefined && alert.value_gbp > 0 && (
                  <span className={clsx('text-xs font-mono font-bold shrink-0', styles.text)}>
                    {fmt(alert.value_gbp)}
                  </span>
                )}
              </div>
            </Wrapper>
          )
        })}
      </div>
    </div>
  )
}
